import {BalloonEventsQuery, mergeQueries} from 'ts-core';
import React, {
    createContext,
    useContext,
    ReactNode,
    useState,
    useRef,
    useEffect,
} from "react";
import {useSearchParams} from "react-router-dom";

export enum QueryIds {
    amount = "amount",
    map = "map",
    dates = "dates",
    text = "text",
    regionArea = "regionArea",
    region = "region",
}

function shouldPopQuery(query: BalloonEventsQuery, other: BalloonEventsQuery) {
    return (query.amount !== undefined && other.amount !== undefined ||
        query.bbox !== undefined && other.bbox !== undefined ||
        query.dates !== undefined && other.dates !== undefined ||
        query.text !== undefined && other.text !== undefined ||
        query.regionArea !== undefined && (other.regionArea !== undefined || other.region !== undefined) ||
        query.region !== undefined && (other.region !== undefined || other.regionArea !== undefined)
        //||
        //query.tags !== undefined && other.tags !== undefined
    )
}

export interface QueryInfo {
    has: (id: string) => boolean;
    query: BalloonEventsQuery;
    queries: BalloonEventsQuery[];
    addQuery: (query: BalloonEventsQuery, alterHistory?: boolean) => void;
    addQueries: (queries: BalloonEventsQuery[], alterHistory?: boolean) => void;
    removeQuery: (query: BalloonEventsQuery, alterHistory?: boolean) => void;
    removeQueryById: (id: string, alterHistory?: boolean) => void;
    clear: (alterHistory?: boolean) => void;
    queryRef: React.MutableRefObject<BalloonEventsQuery>;
    updateHistory: () => void;
}

const QueryContext = createContext<QueryInfo | undefined>(undefined);

type QueryProviderProps = {
    children: ReactNode;
    rootQuery?: BalloonEventsQuery;
    useHistory?: boolean;
    activeQueries?: BalloonEventsQuery[];
};

export const QueryProvider: React.FC<QueryProviderProps> = ({
                                                                children,
                                                                rootQuery,
                                                                useHistory = false,
                                                                activeQueries = []
                                                            }) => {

    const searchParamsInit = useRef(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const queriesRef = useRef<BalloonEventsQuery[]>(getInitialQueries());
    const queryRef = useRef<BalloonEventsQuery>(mergeQueries([rootQuery || {}, ...queriesRef.current]));
    const [query, setQuery] = useState<BalloonEventsQuery>(queryRef.current);
    const [queries, setQueries] = useState<BalloonEventsQuery[]>(queriesRef.current);

    function getInitialQueries() {
        // if search param is set, use it and return it
        // if active query is set, use it and return it
        // if root query is set, use it and return it
        // else return empty object
        if (activeQueries != null && activeQueries.length > 0) {
            return activeQueries
        } else if (searchParams.get("q")) {
            return JSON.parse(searchParams.get("q") || "[]")
        }
        return []
    }

    function updateMergedQuery() {
        const merged = mergeQueries([rootQuery || {}, ...queriesRef.current]);
        queryRef.current = merged;
        setQuery(merged);
    }

    function _addQuery(newQuery: BalloonEventsQuery) {
        // Drop any existing entry with the same id (replace, don't duplicate) in addition
        // to the field-based shouldPopQuery rule. Without this, two async callers racing
        // their `has()` guards (e.g. InitialFilterApplier awaiting the saved-search-by-tag
        // lookup while FilterRouteHandler synchronously fires on the same `/tags/:slug`)
        // both see an empty store and each push an entry, producing duplicate chips.
        queriesRef.current = queriesRef.current.filter(
            q => q.id !== newQuery.id && !shouldPopQuery(q, newQuery)
        )
        queriesRef.current.push(newQuery);
        setQueries(queriesRef.current);

        updateMergedQuery();
    }

    useEffect(() => {
        console.log("useEffect", queriesRef.current)
        if (!useHistory) return;
        if (searchParams.get("q")) {
            console.log("useEffect", "searchParams.get(q)")
            let newQueries: BalloonEventsQuery[] = JSON.parse(searchParams.get("q") || "[]")
            for (const query of newQueries) {
                if (query.dates) {
                    query.dates = query.dates.map((d: any) => {
                        return new Date(d)
                    })
                }
            }
            queriesRef.current = newQueries
            setQueries(queriesRef.current)
            updateMergedQuery();
        } else {
            console.log("useEffect", "searchParams.get(q) not found")
            queriesRef.current = [];
            setQueries([])
            updateMergedQuery();
        }
        searchParamsInit.current = true;
    }, [searchParams])

    function updateHistory() {
        console.log("updateHistory", queriesRef.current)
        if (!useHistory) return;
        updateSearchParams();
    }

    function updateSearchParams(replace = false) {
        console.log("updateSearchParams", queriesRef.current)
        if (!searchParamsInit.current) return;
        /*if (queriesRef.current.length == 0) {
          if ([...searchParams.keys()].length == 0 || Object.keys(searchParams.get("q") || {}).length > 0) return
          searchParams.delete("q")

          setSearchParams(searchParams, { replace: true })
          return
        }*/
        setSearchParams({q: JSON.stringify(queriesRef.current)}, {replace: replace})
    }

    function addQuery(newQuery: BalloonEventsQuery, alterHistory = useHistory) {
        console.log("addQuery", newQuery)
        _addQuery(newQuery)
        if (alterHistory) {
            // push to history
            updateSearchParams()
        }
    }

    function addQueries(newQueries: BalloonEventsQuery[], alterHistory = useHistory) {
        console.log("addQueries", newQueries)
        for (const q of newQueries) {
            _addQuery(q)
        }
        if (alterHistory) {
            // push to history
            updateSearchParams()
        }
    }

    function removeQuery(toRemove: BalloonEventsQuery, alterHistory = useHistory) {
        console.log("removeQuery", toRemove)
        if (!toRemove.id) return;
        removeQueryById(toRemove.id, alterHistory)
    }

    function removeQueryById(id: string, alterHistory = useHistory) {
        console.log("removeQueryById", id)
        queriesRef.current = queriesRef.current.filter(q => q.id !== id)
        setQueries(queriesRef.current);

        updateMergedQuery();

        if (alterHistory) {
            updateSearchParams()
        }
    }


    function clear(alterHistory = useHistory) {
        console.log("clear", queries)
        queriesRef.current = [];
        setQueries([]);
        queryRef.current = rootQuery || {}
        setQuery(queryRef.current);
        if (useHistory && alterHistory) {
            setSearchParams({q: JSON.stringify(queriesRef.current)})
        }
    }

    function has(id: string) {
        if (id === 'labels') {
            console.log("has", id, Object.keys(QueryIds))
            return queriesRef.current.some(c => c.id && !(Object.keys(QueryIds) as string[]).includes(c.id))
        }
        return queriesRef.current.some(c => c.id == id)
    }

    return (
        <QueryContext.Provider
            value={{
                query,
                queries,
                addQuery,
                addQueries,
                removeQuery,
                removeQueryById,
                updateHistory,
                clear,
                has,
                queryRef,
            }}
        >
            {children}
        </QueryContext.Provider>
    );
};

export function useQuery(): QueryInfo {
    const context = useContext(QueryContext);
    if (!context) {
        throw new Error("useCategories must be used within a CategoriesProvider");
    }
    return context;
}
